/**
 * Admin user-management API. Backed by `/api/v1/smt/admin/users` (M2 work,
 * `metsadata/backend/internal/handlers/smt_admin_handler.go`). All routes
 * require an admin JWT — non-admins get a 403 from the backend, which the
 * UI surfaces as "ei käyttöoikeutta".
 */
import { request, requestData } from './client.ts';
import type { User } from '../types/api.ts';
import type { Share } from './shares.ts';

export type UserStatusFilter = 'all' | 'pending' | 'active' | 'suspended' | 'rejected';

export interface ListUsersOptions {
  status?: UserStatusFilter;
}

export async function listUsers(opts: ListUsersOptions = {}): Promise<User[]> {
  const query: Record<string, string> = {};
  if (opts.status && opts.status !== 'all') query.status = opts.status;
  // Backend serialises a nil slice as `null`; coerce so callers can rely on
  // `Array.length` without a defensive guard at every call site.
  const data = await requestData<User[] | null>('/smt/admin/users', { query });
  return data ?? [];
}

export interface ApproveInput {
  level: number;
  allowed_regions?: string[];
  role?: 'user' | 'admin' | 'institution';
  decision_note?: string;
}

export async function approveUser(id: number, input: ApproveInput): Promise<void> {
  await request<unknown>(`/smt/admin/users/${id}/approve`, {
    method: 'PUT',
    body: input,
  });
}

export async function rejectUser(id: number, decision_note?: string): Promise<void> {
  await request<unknown>(`/smt/admin/users/${id}/reject`, {
    method: 'PUT',
    body: { decision_note: decision_note || '' },
  });
}

// ---------------------------------------------------------------------------
// M6.2: Admin "Käyttäjähallinta"-modaalin uudet välilehdet — käyttäjäeditori,
// admin-create, "Jaetut tilat".
// ---------------------------------------------------------------------------

export interface UpdateUserInput {
  level?: number;
  allowed_regions?: string[];
  status?: 'active' | 'pending' | 'rejected' | 'suspended';
  role?: 'user' | 'admin' | 'institution';
  name?: string;
  full_name?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  /** Empty/omitted = no change. Backend enforces min 8 when present. */
  password?: string;
  organization?: string;
  address?: string;
}

/** PUT /smt/admin/users/:id — full editor. */
export async function updateUser(id: number, input: UpdateUserInput): Promise<User> {
  return requestData<User>(`/smt/admin/users/${id}`, {
    method: 'PUT',
    body: input,
  });
}

/** DELETE /smt/admin/users/:id — permanently removes a user. The backend
 *  refuses to delete the calling admin's own account (400). */
export async function deleteUser(id: number): Promise<void> {
  await request<unknown>(`/smt/admin/users/${id}`, { method: 'DELETE' });
}

export interface AdminCreateUserInput {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  organization?: string;
  level: number;
  role: 'user' | 'admin' | 'institution';
  allowed_regions?: string[];
}

/**
 * Create + immediately approve a user from the admin panel. The backend does
 * not yet have a single-call create endpoint, so we orchestrate Register +
 * Approve client-side. Both calls are admin-scoped (the second one needs
 * the admin JWT), so we can't be partially logged in mid-flow.
 */
export async function adminCreateUser(input: AdminCreateUserInput): Promise<User> {
  const reg = await requestData<{ id: number }>('/smt/auth/register', {
    method: 'POST',
    body: {
      email: input.email,
      password: input.password,
      first_name: input.first_name,
      last_name: input.last_name,
      organization: input.organization || '',
      // gdpr_consent is stored as JSONB — must be a valid JSON value.
      // Admin-created accounts skip the GDPR consent flow; we use a JSON
      // string literal so the column accepts it without a cast error.
      gdpr_consent: '"admin-created"',
    },
  });
  // The newly-created row is in 'pending' status; approve it immediately so
  // the admin can log it in without an email round-trip.
  await approveUser(reg.id, {
    level: input.level,
    role: input.role,
    allowed_regions: input.allowed_regions,
    decision_note: 'Admin-created',
  });
  // Return the canonical post-approve user via the list endpoint.
  const fresh = await listUsers({ status: 'active' });
  const found = fresh.find((u) => u.id === reg.id);
  if (found) return found;
  return { ...reg, email: input.email, name: `${input.first_name} ${input.last_name}`.trim(), role: input.role, level: input.level, status: 'active' } as User;
}

/** GET /smt/admin/shares?status=active|revoked|all */
export async function adminListShares(status: 'active' | 'revoked' | 'all' = 'active'): Promise<Share[]> {
  const data = await requestData<Share[] | null>('/smt/admin/shares', {
    query: { status },
  });
  return data ?? [];
}

// ---------------------------------------------------------------------------
// AI-analyysiohje ("metsäsijoittamisen ohjekirja") — the instructions the
// backend passes to the LLM as the system prompt when writing an estate's
// profitability analysis. Backed by `system_settings.analysis_prompt`; an
// absent/blank value means the shipped default is in use.
// ---------------------------------------------------------------------------

export interface AnalysisPrompt {
  prompt: string;
  /** True when no admin override is stored, i.e. the shipped ohjekirja. */
  is_default: boolean;
  /** When an admin last saved or reset it; null if never touched. */
  updated_at: string | null;
}

export async function getAnalysisPrompt(): Promise<AnalysisPrompt> {
  return requestData<AnalysisPrompt>('/smt/admin/analysis-prompt');
}

export async function setAnalysisPrompt(prompt: string): Promise<AnalysisPrompt> {
  return requestData<AnalysisPrompt>('/smt/admin/analysis-prompt', {
    method: 'PUT',
    body: { prompt },
  });
}

/** Drops the override so the shipped ohjekirja takes effect again. */
export async function resetAnalysisPrompt(): Promise<AnalysisPrompt> {
  return requestData<AnalysisPrompt>('/smt/admin/analysis-prompt', {
    method: 'DELETE',
  });
}

export interface ReanalyzeResult {
  id: number;
  name: string;
  analysis: string;
}

/**
 * Re-runs only the analysis stage for one estate from its stored structured
 * data (no scraping), so an admin can verify a prompt edit immediately.
 */
export async function reanalyzeEstate(id: number): Promise<ReanalyzeResult> {
  return requestData<ReanalyzeResult>(`/smt/admin/estates/${id}/reanalyze`, {
    method: 'POST',
  });
}
